export const DynamicIcon = ({
  name,
  type = "solid",
  fallback = <i className="fa-regular fa-face-smile" />,
  className = "",
  style = {},
}) => {
  if (!name || typeof name !== "string" || !name.trim()) {
    return fallback;
  }

  let raw = name.trim();

  // 1. Extract class attribute if client pasted full HTML tag like `<i class="fa-solid fa-house"></i>`
  const classAttrMatch = raw.match(/class(?:Name)?=(?:"|')([^"']+)(?:"|')/i);
  if (classAttrMatch && classAttrMatch[1]) {
    raw = classAttrMatch[1].trim();
  }

  // 2. Parse tokens to extract Free style (fa-solid, fa-regular, fa-brands) & icon name (fa-house)
  const tokens = raw.split(/\s+/);
  let freeStyle = "";
  let iconName = "";

  for (const token of tokens) {
    const t = token.toLowerCase();
    if (t === "fa-brands" || t === "fab") freeStyle = "fa-brands";
    else if (t === "fa-regular" || t === "far") freeStyle = "fa-regular";
    else if (t === "fa-solid" || t === "fas") freeStyle = "fa-solid";
    else if (t.startsWith("fa-")) {
      iconName = t;
    }
  }

  if (freeStyle && iconName) {
    return (
      <i
        className={`${freeStyle} ${iconName} ${className}`.trim()}
        style={style}
      />
    );
  }

  // 3. Single word input or fallback for custom class strings
  let cleanName = raw;
  const prefixMatch = raw.match(/^(fa[srbl]?|fas|far|fab)[-_]+(.+)/i);
  if (prefixMatch) {
    const p = prefixMatch[1].toLowerCase();
    if (p === "far" || p === "fa-regular") freeStyle = "fa-regular";
    else if (p === "fab" || p === "fa-brands") freeStyle = "fa-brands";
    else freeStyle = "fa-solid";
    cleanName = prefixMatch[2];
  } else if (!raw.includes(" ")) {
    cleanName = raw
      .replace(/^(Fa|Fi|Lu)([A-Z])/, "$2")
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1-$2")
      .toLowerCase();
  }

  if (!freeStyle)
    freeStyle = `fa-${type.replace(/^fa[-s]?/i, "").toLowerCase()}`;
  if (!cleanName.startsWith("fa-")) cleanName = `fa-${cleanName}`;

  return (
    <i
      className={`${freeStyle} ${cleanName} ${className}`.trim()}
      style={style}
    />
  );
};

export const getYouTubeId = (url) => {
  if (!url || typeof url !== "string") return "";
  if (
    !url.includes("http://") &&
    !url.includes("https://") &&
    !url.includes(".") &&
    !url.includes("/")
  ) {
    return url; // Direct Video ID string
  }

  const patterns = [
    /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
    /(?:youtu\.be\/)([^?&\s]+)/,
    /(?:youtube\.com\/embed\/)([^?&\s]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return "";
};

export const getYouTubeStartTime = (url) => {
  if (!url || typeof url !== "string") return 0;
  const tMatch = url.match(/[?&]t=([^&]+)/);
  if (!tMatch) return 0;

  const raw = tMatch[1];
  if (/^\d+$/.test(raw)) return parseInt(raw, 10);

  const timeMatch = raw.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/);
  if (!timeMatch) return 0;

  const hours = parseInt(timeMatch[1] || "0", 10);
  const minutes = parseInt(timeMatch[2] || "0", 10);
  const seconds = parseInt(timeMatch[3] || "0", 10);

  return hours * 3600 + minutes * 60 + seconds;
};

export const getEmbedUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.includes("youtube.com/embed/")) return url;

  const videoId = getYouTubeId(url);
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  }
  return url;
};

export const getYouTubeThumbnail = (url) => {
  const fallback =
    "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=800&q=80";
  if (!url || typeof url !== "string") return fallback;

  const videoId = getYouTubeId(url);
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  return fallback;
};

export const buildYouTubeBackgroundEmbedUrl = (rawUrl) => {
  const videoId = getYouTubeId(rawUrl);
  if (!videoId) return null;

  const start = getYouTubeStartTime(rawUrl);

  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    loop: "1",
    playlist: videoId,
    controls: "0",
    showinfo: "0",
    modestbranding: "1",
    rel: "0",
    playsinline: "1",
    iv_load_policy: "3",
    disablekb: "1",
    fs: "0",
    start: String(start),
  });

  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

export const renderTitleWithGradient = (titleText, highlightClass = "") => {
  if (!titleText) return null;
  const cleanStr = String(titleText)
    .replace(/<[^>]*>?/gm, "")
    .trim();
  if (!cleanStr) return null;
  const words = cleanStr.split(/\s+/);
  if (words.length <= 1) {
    return highlightClass ? (
      <span className={highlightClass}>{cleanStr}</span>
    ) : (
      <span>{cleanStr}</span>
    );
  }
  const mainPart = words.slice(0, -1).join(" ");
  const lastWord = words[words.length - 1];
  return (
    <>
      {mainPart}{" "}
      {highlightClass ? (
        <span className={highlightClass}>{lastWord}</span>
      ) : (
        <span>{lastWord}</span>
      )}
    </>
  );
};
